/*
 * Copyright 2024 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/mobile-mobile/blob/master/LICENSE.md
 */
package org.mifos.mobile.core.data.repositoryImpl

import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.withContext
import kotlinx.io.IOException
import org.mifos.mobile.core.common.DataState
import org.mifos.mobile.core.data.repository.UserAuthRepository
import org.mifos.mobile.core.data.util.extractErrorMessage
import org.mifos.mobile.core.model.entity.UpdatePasswordPayload
import org.mifos.mobile.core.model.entity.User
import org.mifos.mobile.core.model.entity.payload.LoginPayload
import org.mifos.mobile.core.model.entity.register.RegisterPayload
import org.mifos.mobile.core.model.entity.register.UserVerify
import org.mifos.mobile.core.network.DataManager

class UserAuthRepositoryImp(
    private val dataManager: DataManager,
    private val ioDispatcher: CoroutineDispatcher,
) : UserAuthRepository {

    override suspend fun registerUser(
        registerPayload: RegisterPayload,
    ): DataState<String> {
        return withContext(ioDispatcher) {
            try {
                val response = dataManager.registrationApi.registerUser(registerPayload)
                DataState.Success(response.bodyAsText())
            } catch (e: ClientRequestException) {
                val errorMessage = extractErrorMessage(e.response)
                DataState.Error(Exception(errorMessage), null)
            } catch (e: IOException) {
                DataState.Error(Exception("Network error: ${e.message ?: "Please check your connection"}"), null)
            } catch (e: ServerResponseException) {
                DataState.Error(Exception("Server error: ${e.message}"), null)
            }
        }
    }

    override suspend fun login(username: String, password: String): DataState<User> {
        val loginPayload = LoginPayload(
            username = username,
            password = password,
        )
        return try {
            withContext(ioDispatcher) {
                val user = dataManager.authenticationApi.authenticate(loginPayload)
                if (user.base64EncodedAuthenticationKey != null) {
                    DataState.Success(user)
                } else {
                    DataState.Error(Exception("Invalid Credentials"), null)
                }
            }
        } catch (e: ClientRequestException) {
            val errorMessage = extractErrorMessage(e.response)
            DataState.Error(Exception(errorMessage), null)
        } catch (e: IOException) {
            DataState.Error(Exception("Network error: ${e.message ?: "Please check your connection"}"), null)
        } catch (e: ServerResponseException) {
            DataState.Error(Exception("Server error: ${e.message}"), null)
        }
    }

    override suspend fun verifyUser(
        authenticationToken: String?,
        requestId: String?,
    ): DataState<String> {
        val userVerify = UserVerify(
            authenticationToken = authenticationToken,
            requestId = requestId,
        )
        return withContext(ioDispatcher) {
            try {
                val response = dataManager.registrationApi.verifyUser(userVerify)
                DataState.Success(response.bodyAsText())
            } catch (e: ClientRequestException) {
                val errorMessage = extractErrorMessage(e.response)
                DataState.Error(Exception(errorMessage), null)
            } catch (e: IOException) {
                DataState.Error(Exception("Network error: ${e.message ?: "Please check your connection"}"), null)
            } catch (e: ServerResponseException) {
                DataState.Error(Exception("Server error: ${e.message}"), null)
            }
        }
    }

    override suspend fun updateAccountPassword(
        newPassword: String,
        confirmPassword: String,
    ): DataState<String> {
        val payload = UpdatePasswordPayload(
            password = newPassword,
            repeatPassword = confirmPassword,
        )
        return withContext(ioDispatcher) {
            try {
                val response = dataManager.userDetailsApi.updateAccountPassword(payload)
                DataState.Success(response.bodyAsText())
            } catch (e: ClientRequestException) {
                val errorMessage = extractErrorMessage(e.response)
                DataState.Error(Exception(errorMessage), null)
            } catch (e: IOException) {
                DataState.Error(Exception("Network error: ${e.message ?: "Please check your connection"}"), null)
            } catch (e: ServerResponseException) {
                DataState.Error(Exception("Server error: ${e.message}"), null)
            }
        }
    }
}
